import { useCliftContext } from "../../hooks/useCliftContext";
import { CliftReducerAction } from "../../context/clift-context/clift-action-types";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { toast } from "react-toast";
import SideModal from "../../components/SideModal";
import { Customer } from "../../models/customer";
import { CustomerForm } from "./CustomerForm";
import { CustomerFormValues } from "../../models/customer";
import { updateCustomer } from "../../services/customers-api";
import { Contact } from "../../models/contact";
import {
  getLocalizedErrorReason,
  getErrorCode,
} from "../../services/clift-api-errors";
import { SideModalButtonBarProps } from "../../components/SideModalButtonBar";

export interface EditCustomerProps {
  page?: number | undefined;
  editingCustomer: Customer | undefined;
  afterSuccessfullEdit?: () => void;
}

export const EditCustomer = ({
  page,
  editingCustomer,
  afterSuccessfullEdit,
}: EditCustomerProps) => {
  const { cliftState, dispatchCliftState } = useCliftContext();
  const [currentPageIndex, setCurrentPageIndex] = useState(page ?? 0);
  const { t } = useTranslation();
  const [bottomButtonsProps, setBottomButtonsProps] =
    useState<SideModalButtonBarProps>();

  useEffect(() => {
    if (editingCustomer) {
      dispatchCliftState({
        type: CliftReducerAction.FetchContacts,
        tenantID: cliftState.currentTenant?.id,
        customerID: editingCustomer.id,
      });
    }
  }, [editingCustomer]);

  if (!editingCustomer) return null;

  const closeSidePanel = () => {
    dispatchCliftState({
      type: CliftReducerAction.SetFromOriginalPage,
      fromOriginalPage: "/customers",
    });

    dispatchCliftState({
      type: CliftReducerAction.CloseSidePanel,
    });
  };

  const onUpdateCustomer = (
    formValues: CustomerFormValues,
    contacts: Contact[]
  ) => {
    if (!editingCustomer) {
      throw new Error("No current customer");
    }

    return updateCustomer(
      editingCustomer,
      formValues,
      cliftState.currentTenant!.id,
      contacts
    )
      .then(() => {
        afterSuccessfullEdit?.();
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("customer_edit_success", {
            ns: "alerts",
            customerId: editingCustomer.id,
          }),
        });
        toast.success(
          t("changes_saved", {
            ns: "toasts",
          })
        );
      })
      .catch((err) => {
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("customer_edit_failed", {
            ns: "alerts",
            customerId: editingCustomer.id,
            code: getErrorCode(err),
          }),
        });
        toast.error(
          t("error") +
            " " +
            t("edit_failed", {
              ns: "toasts",
              reason: getLocalizedErrorReason(err),
            })
        );
      });
  };

  const getCustomerIsAccountOwner = (customer: Customer) => {
    return (
      customer.accountOwner?.firstName === customer.firstName &&
      customer.accountOwner?.lastName === customer.lastName &&
      customer.accountOwner?.email === customer.email &&
      customer.accountOwner?.phoneNr == customer.phoneNr
    );
  };

  return (
    <SideModal
      title={t("edit_customer", { ns: "customer" })}
      pageCount={2}
      pageIndex={currentPageIndex}
      bottomButtonsProps={bottomButtonsProps}
      onClose={closeSidePanel}
    >
      <CustomerForm
        pageIndex={currentPageIndex}
        initialValues={{
          ...editingCustomer,
          customerIsAccountOwner: getCustomerIsAccountOwner(editingCustomer),
        }}
        onSubmit={onUpdateCustomer}
        onClose={closeSidePanel}
        onSwitchPage={setCurrentPageIndex}
        editingCustomer={editingCustomer}
        showSaveOnFirstPage={true}
        setBottomButtonsProps={setBottomButtonsProps}
      />
    </SideModal>
  );
};
